import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { useTreatmentFilters } from '@client/feature/treatment/hooks/useTreatmentFilters';
import { useTreatments } from '@client/feature/treatment/hooks/useTreatment';
import { useFilterAggregations } from '@client/hooks/useFilterAggregations';
import {
  BackgroundSection,
  FilterPanels,
} from '@client/feature/treatment/partials';
import Result from '@client/feature/treatment/components/Search/Result/Result';
import InitialCriteriaPanel from '@client/feature/treatment/components/InitialCriteriaPanel';

const TreatmentResults = ({ searchData, onBackToSteps }) => {
  const { t } = useTranslation('treatment-search');

  const {
    filters,
    filterLabels,
    updateFilterWithLabels,
    updatePagination,
    updateSorting,
    getCurrentSorting,
    resetFilters,
    hasActiveFilters,
    getFilterLabel,
    shareToClipboard,
    isLoadingLabels,
  } = useTreatmentFilters();

  // Merge pre-filters from searchData with current filters
  const mergedFilters = useMemo(() => {
    const preFilters = searchData?.selections || {};
    const transformedPreFilters = {};

    // Only add non-empty filters
    Object.entries(preFilters).forEach(([field, values]) => {
      // Skip if values is null, undefined, or empty array
      if (!values || (Array.isArray(values) && values.length === 0)) {
        return;
      }

      switch (field) {
        case 'treatment_cultures.culture':
          transformedPreFilters['cultures'] = values;
          break;
        case 'treatment_cultures.cultural_practices':
          transformedPreFilters['cultural_practices'] = values;
          break;
        case 'treatment_type':
          transformedPreFilters['treatment_types'] = values;
          break;
        case 'application_type':
          transformedPreFilters['application_types'] = values;
          break;
        case 'intervention_period':
          transformedPreFilters['intervention_periods'] = values;
          break;
        default:
          transformedPreFilters[field] = values;
      }
    });

    return {
      ...filters,
      ...transformedPreFilters,
    };
  }, [searchData?.selections, filters]);

  const { data, isLoading, error } = useTreatments(mergedFilters);
  const aggregations = useFilterAggregations(data, mergedFilters);

  const handleBackToSteps = useCallback(() => {
    onBackToSteps();
  }, [onBackToSteps]);

  const handlePageChange = useCallback(
    ({ page, size }) => {
      updatePagination({ page, size });
    },
    [updatePagination]
  );

  const handleSorting = useCallback(
    (field, order) => updateSorting(field, order),
    [updateSorting]
  );

  const handleReset = useCallback(() => resetFilters(), [resetFilters]);
  const handleShare = useCallback(() => shareToClipboard(), [shareToClipboard]);

  const handleRemoveFilter = useCallback(
    (filterKey, valueId) => {
      const currentValues = filters[filterKey] || [];
      const newValues = currentValues.filter((v) => v !== valueId);
      updateFilterWithLabels(filterKey, newValues);
    },
    [filters, updateFilterWithLabels]
  );

  // Show loading spinner during cold start label resolution
  if (isLoadingLabels) {
    return (
      <div className="relative w-full min-h-screen">
        <div className="container">
          <BackgroundSection />
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen">
      <div className="container">
        <BackgroundSection />
        <div className="flex flex-col gap-8 relative z-10">
          <InitialCriteriaPanel
            searchData={searchData}
            onBackToSteps={handleBackToSteps}
          />

          <div className="bg-white p-8 rounded-[32px] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
            <FilterPanels
              filters={filters}
              onFilterChange={updateFilterWithLabels}
              aggregations={aggregations}
              filterLabels={filterLabels}
            />
          </div>

          <Result
            filters={filters}
            searchData={searchData}
            data={data}
            isLoading={isLoading}
            error={error}
            getFilterLabel={getFilterLabel}
            onResetFilters={handleReset}
            onRemoveFilter={handleRemoveFilter}
            onPageChange={handlePageChange}
            onSortChange={handleSorting}
            currentSorting={getCurrentSorting()}
            onShare={handleShare}
          />
        </div>
        {!isLoading && (
          <>
            <p className="mb-4">
              {t(
                "treatment|Note 1 : Les symboles de risque pour la santé sont basés sur l'effet le plus critique de la matière active."
              )}
            </p>
            <p className="mb-4">
              {t(
                "treatment|Note 2 : Les symboles de risque pour l'environnement sont associés aux matières actives comprises dans les produits et non aux traitements. Pour obtenir les symboles de risque associés aux traitements et pour les indices de risque, utilisez les outils de SAgE pesticides qui permettent ces calculs."
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default TreatmentResults;
